import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import './WinProbability.css';

/**
 * WinProbability — live-vinstchans för team1 som delad bar + rundkurva.
 *
 * Baren visar aktuell kartvinstchans (team1 vänster, team2 höger). Kurvan
 * ritar sannolikheten per rättad rund (rund 0 = Elo-priorn vid matchstart)
 * med en 50 %-referenslinje. En serie → ingen legend; identiteten bärs av
 * lagnamnen i barens etiketter.
 *
 * @param {{
 *   probability: number|null,
 *   history: Array<{round: number, map_number: number, probability: number,
 *     team1_score: number, team2_score: number}>,
 *   team1Name: string,
 *   team2Name: string,
 *   hideBar?: boolean,
 * }} props Aktuell sannolikhet (0–1 för team1), kurvpunkter och lagnamn.
 *   `hideBar` döljer den aktuella baren — används på färdiga matchsidor där
 *   utfallet är känt och bara den historiska kurvan är intressant.
 * @returns {JSX.Element|null} Panelen, eller null utan data.
 */
const WinProbability = ({ probability, history, team1Name, team2Name, hideBar = false }) => {
  const { t } = useTranslation();

  const points = history || [];
  const effective = probability ?? (points.length ? points[points.length - 1].probability : null);
  if (effective === null || effective === undefined) return null;

  const pct1 = Math.round(effective * 100);
  const pct2 = 100 - pct1;

  // Kurvan får ett löpande index som x-värde så BO3-kartor inte kolliderar
  // (rundnumren börjar om per karta); tooltipen visar karta + rund + score.
  const chartData = (history || []).map((point, index) => ({
    index,
    round: point.round,
    mapNumber: point.map_number,
    pct: Math.round((point.probability ?? 0) * 100),
    score: `${point.team1_score}–${point.team2_score}`,
  }));

  return (
    <div className="win-probability">
      <div className="win-prob-header">
        <span className="win-prob-title">📈 {t('winprob.title')}</span>
      </div>

      {!hideBar && (
        <div className="win-prob-bar-row">
          <span className="win-prob-team win-prob-team1">
            {team1Name} <strong>{pct1}%</strong>
          </span>
          <div className="win-prob-bar" role="img" aria-label={`${team1Name} ${pct1}% – ${team2Name} ${pct2}%`}>
            <div className="win-prob-bar-fill" style={{ width: `${pct1}%` }} />
            <div className="win-prob-bar-mid" />
          </div>
          <span className="win-prob-team win-prob-team2">
            <strong>{pct2}%</strong> {team2Name}
          </span>
        </div>
      )}

      {chartData.length >= 2 && (
        <div className="win-prob-chart">
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="winProbFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="index"
                tick={{ fill: '#a0a0b0', fontSize: 10 }}
                axisLine={{ stroke: '#2a2a3a' }}
                tickLine={false}
                tickFormatter={(i) => {
                  const point = chartData[i];
                  return point && point.round > 0 ? `R${point.round}` : '';
                }}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 50, 100]}
                tick={{ fill: '#a0a0b0', fontSize: 10 }}
                axisLine={{ stroke: '#2a2a3a' }}
                tickLine={false}
                width={34}
                tickFormatter={(v) => `${v}%`}
              />
              <ReferenceLine y={50} stroke="#2a2a3a" strokeDasharray="4 4" />
              <Tooltip
                contentStyle={{
                  background: '#1a1a25',
                  border: '1px solid #2a2a3a',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                }}
                formatter={(value) => [`${value}%`, team1Name]}
                labelFormatter={(i) => {
                  const point = chartData[i];
                  if (!point) return '';
                  return point.round > 0
                    ? `${t('winprob.round')} ${point.round} (${point.score})`
                    : t('winprob.start');
                }}
              />
              <Area
                type="monotone"
                dataKey="pct"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#winProbFill)"
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default WinProbability;
